import React, { useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Alert,
  CircularProgress,
  Box,
  MenuItem
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

// ✅ IMPORTS
import { countryCodes, defaultCountryCode } from "../constants/countryCodes";
import { indianStates } from "../constants/indianStates";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://swadvedamasalasbe.onrender.com/api";

const userTypes = [
  { value: "customer", label: "Customer" },
  { value: "vendor", label: "Vendor" }
];

const Signup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    userType: "customer",
    mobileCountryCode: defaultCountryCode,
    mobile: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
    country: "India", // ✅ FIXED
    password: "",
    confirmPassword: "",
    referralCode: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    const formattedValue =
      name === "mobile"
        ? value.replace(/\D/g, "").slice(0, 10)
        : name === "pincode"
        ? value.replace(/\D/g, "").slice(0, 6)
        : value;

    setForm({ ...form, [name]: formattedValue });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) {
      return setError("Passwords do not match");
    }

    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/auth/signup`, {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        userType: form.userType,
        mobileCountryCode: form.mobileCountryCode,
        mobile: form.mobile,
        phone: `${form.mobileCountryCode}${form.mobile}`,
        address: form.street,
        city: form.city,
        state: form.state,
        postalCode: form.pincode,
        country: form.country,
        password: form.password,
        referralCode: form.referralCode || undefined
      });

      const token = res.data?.token;

      if (token) {
        await login(token);
        navigate("/");
      } else {
        navigate("/login");
      }

    } catch (err) {
      setError(err.response?.data?.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container sx={{ minHeight: "100vh" }}>

      {/* LEFT SIDE */}
      <Grid
        item xs={12} md={5}
        sx={{
          position: "relative",
          backgroundImage: `url('/images/auth-spices.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff"
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(139,0,0,0.8), rgba(0,0,0,0.7))"
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", px: 4 }}>
          <Typography variant="h3" fontWeight="bold">
            Join Swadveda
          </Typography>
        </Box>
      </Grid>

      {/* RIGHT SIDE */}
      <Grid item xs={12} md={7} display="flex" justifyContent="center" alignItems="center">
        <Paper sx={{ p: 5, width: "100%", maxWidth: 650, borderRadius: 3 }}>

          <Typography variant="h5" mb={3}>
            Create your account
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSignup}>
            <Grid container spacing={3}>

              {/* NAME */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" name="firstName" value={form.firstName} onChange={handleChange} required />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} required />
              </Grid>

              {/* EMAIL */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" name="email" value={form.email} onChange={handleChange} required />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="User Type"
                  name="userType"
                  value={form.userType}
                  onChange={handleChange}
                  required
                >
                  {userTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* MOBILE */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    select
                    label="Code"
                    name="mobileCountryCode"
                    value={form.mobileCountryCode}
                    onChange={handleChange}
                    sx={{ width: 120 }}
                  >
                    {countryCodes.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.code}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    label="Mobile Number"
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    required
                  />
                </Box>
              </Grid>

              {/* ADDRESS */}
              <Grid item xs={12}>
                <TextField fullWidth label="Street Address" name="street" value={form.street} onChange={handleChange} required />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="City" name="city" value={form.city} onChange={handleChange} required />
              </Grid>

              {/* ✅ STATE DROPDOWN */}
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="State"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  required
                >
                  {indianStates.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Pincode" name="pincode" value={form.pincode} onChange={handleChange} required />
              </Grid>

              {/* COUNTRY */}
              <Grid item xs={12}>
                <TextField fullWidth label="Country" name="country" value={form.country} onChange={handleChange} required />
              </Grid>

              {/* REFERRAL CODE (OPTIONAL) */}
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  label="Referral Code (Optional)" 
                  name="referralCode" 
                  value={form.referralCode} 
                  onChange={handleChange}
                  placeholder="Enter referral code if you have one"
                  helperText="Get ₹100 bonus if you have a referral code"
                />
              </Grid>

              {/* PASSWORD */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Password" type="password" name="password" value={form.password} onChange={handleChange} required />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Confirm Password" type="password" name="confirmPassword" value={form.confirmPassword} onChange={handleChange} required />
              </Grid>

            </Grid>

            <Button fullWidth type="submit" variant="contained" sx={{ mt: 4 }}>
              {loading ? <CircularProgress size={24} /> : "Create Account"}
            </Button>
          </form>

          <Button fullWidth sx={{ mt: 2 }} onClick={() => navigate("/login")}>
            Already have an account? Login
          </Button>

        </Paper>
      </Grid>
    </Grid>
  );
};

export default Signup;
